<template>
  <div
    v-scroll-into-view="
      scrollToTopAt
        ? { behavior: 'smooth', block: usingNewLayout ? 'start' : 'end' }
        : false
    "
    class="account-mapping__outer-container"
  >
    <AccountMappingBanners />
    <div class="account-mapping">
      <div v-if="!usingNewLayout" class="account-mapping__page-header">
        <AccountMappingPageHeader
          :authenticity-token="authenticityToken"
          :save-url="url"
          :async-save-url="asyncSaveUrl"
          :save-method="method"
          :save-batch-size="saveBatchSize"
          :ai-mode="isAiMappingProposalSupported"
        />
      </div>
      <template v-if="isLoading">
        <div class="account-mapping__loading-container">
          <div class="sf-spinner sf-spinner-primary sf-spinner-l" />
          <div
            v-if="isAiMappingProposalSupported && shouldUseAutomaticSuffix"
            class="account-mapping__loading-text"
          >
            <div>{{ I18n.t('account_mapping_list.loading.prompt1') }}</div>
            <div>{{ I18n.t('account_mapping_list.loading.prompt2') }}</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="account-mapping__right-container">
          <AccountTables
            :mapping-list-account-types="mappingListAccountTypes"
          />
        </div>
      </template>
    </div>
    <SfButtonDs
      variant="secondary"
      icon-only
      icon-before="arrow-up"
      :aria-label="I18n.t('account_mapping_list.back_to_top')"
      class="account-mapping__back-to-top !tw-rounded-full"
      data-testid="back-to-top-button"
      @click="scrollToTop"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import { SfButtonDs } from '@silverfin/ui-components'
import AccountMappingBanners from './account-mapping-banners'
import AccountMappingPageHeader from './account-mapping-page-header'
import scrollIntoView from '@app/shared/vue/directives/scrollIntoView'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import AccountTables from './account-tables'
import { capitaliseLocale } from '@app/application/account-mapping/utils'
import Polling from '@app/utils/polling'
import { showErrorBanner } from '@app/application/account-mapping/utils'
import { maxWaitRecommendationsTimeout } from '../constants'
import {
  fetchAIMappingProposalSet,
  waitForAIScheduling,
} from '@app/application/account-mapping/api'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useLocale } from '@app/application/account-mapping/store/locale'
import { useRecommendations } from '@app/application/account-mapping/store/recommendations'
import { useBannerManager } from '@app/shared/pinia/banner-manager'
import { bannerVariants } from '@app/shared/types/banner'

export default {
  components: {
    AccountMappingBanners,
    AccountMappingPageHeader,
    AccountTables,
    SfButtonDs,
  },
  directives: {
    scrollIntoView,
  },
  props: {
    authenticityToken: {
      type: String,
      required: true,
    },
    externalMappingsAvailable: {
      type: Boolean,
      default: false,
    },
    isAiMappingProposalSupported: {
      type: Boolean,
    },
    mappingListAccountTypes: {
      type: Array,
      default: () => [],
    },
    messageBusChannelName: {
      type: String,
      default: '',
    },
    messageBusLastId: {
      type: Number,
      default: 0,
    },
    method: {
      type: String,
      required: true,
    },
    saveBatchSize: {
      type: Number,
      required: true,
    },
    shouldUseAutomaticSuffix: {
      type: Boolean,
    },
    shouldUseOriginalNames: {
      type: Boolean,
    },
    supportedLocales: {
      type: Array,
      default: () => [],
    },
    url: {
      type: String,
      required: true,
    },
    asyncSaveUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      scrollToTopAt: null,
    }
  },
  computed: {
    ...mapState(useCommon, ['isFetching']),
    ...mapState(useRecommendations, ['isWaitingForRecommendations']),
    usingNewLayout() {
      return isUsingNewLayout()
    },
    isLoading() {
      if (this.isAiMappingProposalSupported) {
        return this.isFetching || this.isWaitingForRecommendations
      } else {
        return this.isFetching
      }
    },
  },
  watch: {
    isFetching(newValue, oldValue) {
      if (
        newValue !== oldValue &&
        !newValue &&
        this.isAiMappingProposalSupported
      ) {
        // As soon as initial data is fetched, apply the recommendations and auto map the relevant accounts in case they have arrived sooner
        this.autoMapRecommendations()
      }
    },
  },

  async created() {
    this.usePostfixes(this.shouldUseAutomaticSuffix)

    const capitalizedLocales = [
      window.I18n.locale,
      ...(this.supportedLocales || []),
    ].map(capitaliseLocale)

    this.setSupportedLocales([...new Set(capitalizedLocales)])

    this.fetchInitialData({
      shouldUsePostfixes: this.shouldUseAutomaticSuffix,
    })

    this.fetchAccountBalancesData()

    this.useOriginalNames(this.shouldUseOriginalNames)

    //Only do proposals when it's supported and suffixes are enabled
    if (this.isAiMappingProposalSupported && this.shouldUseAutomaticSuffix) {
      await this.AIProposalSchedule()
    }
    if (this.isAiMappingProposalSupported && !this.shouldUseAutomaticSuffix) {
      const bannerStore = useBannerManager()
      bannerStore.raiseBanner({
        id: 'data.error_message',
        variant: bannerVariants.warning,
        title: window.I18n.t('account_mapping_list.suffixes_warning'),
      })
    }
  },
  beforeUnmount() {
    if (this.isAiMappingProposalSupported) {
      this.unsubscribeFromRecommendations()
    }
  },
  methods: {
    ...mapActions(useCommon, [
      'fetchInitialData',
      'fetchAccountBalancesData',
      'useOriginalNames',
      'usePostfixes',
    ]),
    ...mapActions(useRecommendations, [
      'addAccountToGroupFromRecommendations',
      'autoMapRecommendations',
      'digestRecommendations',
      'startWaitingForRecommendations',
      'stopWaitingForRecommendations',
    ]),
    ...mapActions(useLocale, ['setSupportedLocales']),
    scrollToTop() {
      this.scrollToTopAt = Date.now()
    },
    async AIProposalSchedule() {
      this.startWaitingForRecommendations()

      this.clearRecommendationsTimeout = setTimeout(() => {
        this.stopWaitingForRecommendations()
      }, maxWaitRecommendationsTimeout)

      try {
        await waitForAIScheduling()

        this.subscribeToRecommendations()
      } catch (error) {
        this.stopWaitingForRecommendations()

        showErrorBanner(
          error.body?.errorMessage,
          error.body?.errorMessagePerLocale
        )

        if (window.Sentry) {
          if (error === 'SFetch::WithRetry::Max retries exceeded') {
            window.Sentry.captureMessage(
              'Max retries exceeded for AI Mapping scheduling',
              {
                level: 'info',
                extra: {
                  companyId: window.Silverfin.currentCompanyId,
                  firmId: window.Silverfin.currentFirmId,
                },
              }
            )
          } else {
            window.Sentry.captureException(error)
          }
        }
      }
    },
    subscribeToRecommendations() {
      this.polling = new Polling(
        this.messageBusChannelName,
        parseInt(this.messageBusLastId || 0, 10),
        (message) => this.handleRecommendationsMessage(message)
      )

      this.polling.subscribe()
    },
    async handleRecommendationsMessage(message) {
      // Unsubscribe before awaiting: the bus client re-reads its callback list per
      // message, so a second publish in the same batch would arrive mid-fetch and
      // digest a run this screen was never told about.
      if (this.polling) {
        this.polling.unsubscribe()
      }

      try {
        const payload =
          message?.status === 'ready'
            ? await fetchAIMappingProposalSet(message.proposal_set_uuid)
            : message

        if (payload) {
          this.digestRecommendations(payload)
        } else {
          // An expired or unknown set answers 204. Without this the spinner just
          // stops and the user is given no reason.
          showErrorBanner()
        }
      } catch (error) {
        showErrorBanner()

        if (window.Sentry) {
          window.Sentry.captureException(error)
        }
      } finally {
        this.unsubscribeFromRecommendations()
      }
    },
    unsubscribeFromRecommendations() {
      this.stopWaitingForRecommendations()

      if (this.polling) {
        this.polling.unsubscribe()
      }

      clearTimeout(this.clearRecommendationsTimeout)
    },
  },
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.account-mapping {
  padding-bottom: 60px;

  &__back-to-top {
    position: fixed;
    bottom: 1%;
    right: 1%;
  }

  &__outer-container {
    max-height: calc(100vh - 50px);

    .two-drawer-layout-new & {
      max-height: none;

      :deep(.sf-table-sticky th) {
        --sticky-offset: -25px;
      }
    }
  }

  &__page-header {
    grid-area: mapping-header;
  }

  &__loading {
    &-container {
      grid-area: 2 / 1 / 4 / 3;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 15px;
      padding-top: 10%;
    }

    &-text {
      @include font-size('heading', 'm');
      @include color('text');

      max-width: max(30%, 400px);
      line-height: 1.3;
      text-align: center;
    }
  }

  &__left {
    &-container {
      @include background-color('snow', 'light');

      grid-area: account-list-content;
      overflow-y: auto;
    }

    &-header {
      @include background-color('white');

      display: flex;
      align-items: center;
      grid-area: account-list-header;
      border-bottom: 1px solid -color('snow');
      border-right: 1px solid -color('snow');
    }
  }

  &__right {
    &-container {
      @include background-color('snow');

      grid-area: account-group-list-content;
      padding: 0 space(2) 0 space(3);

      // #main-content-pane owns the page background under the new navigation,
      // so drop this container's own background to avoid repainting over it.
      .two-drawer-layout-new & {
        background-color: transparent;
      }
    }

    &-header {
      @include background-color('snow');

      display: flex;
      align-items: center;
      grid-area: account-group-list-header;
    }
  }
}
</style>
